/**
 * Per-voice filter block: lp/hp/bp biquad, formant (two parallel band-passes morphing between vowels from the
 * ticker), or comb (DelayNode of an integer number of periods + feedback, key-tracked to the voice pitch).
 * Cutoff = drone cutoff × macro brightness × key tracking × gesture brightness × drift, always via setTargetAtTime.
 */
import type { Drone } from '@tonist/shared';
import { LfoRoute, Perlin1D } from './modulation';
import { ticker } from './modulation';
import { COMB_FEEDBACK_MAX, LOOP_LOWPASS_Q_DB, clamp, combDelaySeconds, combWetScale, glideTo, keyTrackScale, softClipCurve, targetTau, type MacroMap } from './params';

type FilterSpec = Drone['filter'];

/** F1/F2 pairs for A, E, I, O, U (Hz). */
export const VOWELS: Array<[number, number]> = [[730, 1090], [530, 1840], [390, 1990], [570, 840], [440, 1020]];

/** Interpolated formant pair for a continuous vowel position (cycles through the table). */
export function vowelFormants(pos: number): [number, number] {
  const n = VOWELS.length;
  const p = ((pos % n) + n) % n;
  const i = Math.floor(p);
  const f = p - i;
  const a = VOWELS[i]!;
  const b = VOWELS[(i + 1) % n]!;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

export class VoiceFilter {
  readonly input: GainNode;
  readonly output: GainNode;
  private readonly kind: FilterSpec['type'];
  private biquad: BiquadFilterNode | null = null;
  private formant: { f1: BiquadFilterNode; f2: BiquadFilterNode } | null = null;
  private comb: { delay: DelayNode; fb: GainNode; damp: BiquadFilterNode; wet: GainNode; clip: WaveShaperNode } | null = null;
  private lfo: LfoRoute | null = null;
  private untick: (() => void) | null = null;
  private spec: FilterSpec;
  private macros: MacroMap;
  private gesture = 1;
  private drift = 1;
  private hz = 220;

  constructor(private readonly ctx: AudioContext, spec: FilterSpec, macros: MacroMap, seed: number) {
    this.spec = spec;
    this.macros = macros;
    this.kind = spec.type;
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    if (spec.type === 'lp' || spec.type === 'hp' || spec.type === 'bp') {
      const b = ctx.createBiquadFilter();
      b.type = spec.type === 'lp' ? 'lowpass' : spec.type === 'hp' ? 'highpass' : 'bandpass';
      b.frequency.value = this.effectiveCutoff();
      b.Q.value = spec.q;
      this.input.connect(b);
      b.connect(this.output);
      this.biquad = b;
      if (spec.lfo.depth > 0 && spec.lfo.rateHz > 0) {
        this.lfo = new LfoRoute(ctx, { shape: spec.lfo.shape, rateHz: spec.lfo.rateHz, depth: this.lfoDepthHz(), phase: (seed % 997) / 997, seed }, b.frequency);
      }
    } else if (spec.type === 'formant') {
      const f1 = ctx.createBiquadFilter();
      const f2 = ctx.createBiquadFilter();
      f1.type = f2.type = 'bandpass';
      f1.Q.value = f2.Q.value = 6;
      const [a, b] = vowelFormants(0);
      f1.frequency.value = a;
      f2.frequency.value = b;
      const boost = ctx.createGain();
      boost.gain.value = 2;
      this.input.connect(f1); this.input.connect(f2);
      f1.connect(boost); f2.connect(boost);
      boost.connect(this.output);
      this.formant = { f1, f2 };
      const perlin = new Perlin1D(seed);
      let pos = (seed % 5000) / 1000;
      this.untick = ticker.add((now, dt) => {
        const rate = this.spec.lfo.rateHz > 0 ? this.spec.lfo.rateHz : 0.07;
        pos += rate * dt * (0.5 + 0.5 * this.macros.lfoDepthScale);
        const v = (perlin.at(pos) + 1) * 2.5; // 0..5 vowel position
        const [x, y] = vowelFormants(v);
        const kt = keyTrackScale(this.hz, this.spec.keyTrack * 0.5);
        targetTau(f1.frequency, x * kt, now, 0.12);
        targetTau(f2.frequency, y * kt, now, 0.12);
      });
    } else if (spec.type === 'comb') {
      // Keyed comb ("jivari"). Loop: delay → damp (peak-free lowpass) → soft clip → fb → delay. The loop gain is
      // fb × |H| < 1, and the clipper bounds it absolutely. The wet tap is scaled by (1 − fb) so the resonant peak
      // never exceeds ≈ +6 dB however far the feedback is pushed (it used to reach +30 dB and then run away).
      const delay = ctx.createDelay(1);
      delay.delayTime.value = combDelaySeconds(this.hz, ctx.sampleRate);
      const fbValue = clamp(spec.combFeedback, 0, COMB_FEEDBACK_MAX);
      const fb = ctx.createGain();
      fb.gain.value = fbValue;
      const damp = ctx.createBiquadFilter();
      damp.type = 'lowpass';
      damp.Q.value = LOOP_LOWPASS_Q_DB;
      damp.frequency.value = clamp(spec.cutoffHz, 500, 12000);
      const clip = ctx.createWaveShaper();
      clip.curve = softClipCurve();
      const wet = ctx.createGain();
      wet.gain.value = (1 - fbValue) * combWetScale(this.hz, ctx.sampleRate);
      // The loop is a pure delay + clipper + gain so its resonances land exactly on n·f0/k (a filter inside the loop
      // adds phase delay and pulls them ~25 cents flat); the damping lowpass sits on the wet tap instead.
      this.input.connect(this.output);
      this.input.connect(delay);
      delay.connect(clip);
      clip.connect(fb);
      fb.connect(delay);
      fb.connect(damp);
      damp.connect(wet);
      wet.connect(this.output);
      this.comb = { delay, fb, damp, wet, clip };
    } else {
      this.input.connect(this.output);
    }
  }

  private lfoDepthHz(): number {
    return this.spec.lfo.depth * this.spec.cutoffHz * 0.9 * this.macros.lfoDepthScale;
  }

  private effectiveCutoff(): number {
    const nyq = this.ctx.sampleRate * 0.45;
    return clamp(this.spec.cutoffHz * this.macros.cutoffScale * keyTrackScale(this.hz, this.spec.keyTrack) * this.gesture * this.drift, 20, Math.min(18000, nyq));
  }

  private applyCutoff(t: number, seconds: number): void {
    if (this.biquad) glideTo(this.biquad.frequency, this.effectiveCutoff(), t, seconds);
    if (this.comb) glideTo(this.comb.damp.frequency, clamp(this.spec.cutoffHz * this.macros.cutoffScale * this.gesture, 500, 12000), t, seconds);
  }

  setPitch(hz: number, t: number, glideSec: number): void {
    this.hz = hz;
    if (this.spec.keyTrack > 0) this.applyCutoff(t, Math.max(0.05, glideSec));
    if (this.comb) {
      glideTo(this.comb.delay.delayTime, combDelaySeconds(hz, this.ctx.sampleRate), t, Math.max(0.02, glideSec));
      targetTau(this.comb.wet.gain, (1 - this.comb.fb.gain.value) * combWetScale(hz, this.ctx.sampleRate), t, Math.max(0.05, glideSec));
    }
  }

  /** gesture radius → brightness multiplier (0.25..4) */
  setBrightness(scale: number, t: number): void {
    this.gesture = scale;
    this.applyCutoff(t, 0.08);
  }

  setDrift(mult: number, t: number): void {
    this.drift = mult;
    this.applyCutoff(t, 0.25);
  }

  /** gesture radius → comb feedback (jivari) */
  setJivari(feedback: number, t: number): void {
    if (!this.comb) return;
    const fb = clamp(feedback, 0, COMB_FEEDBACK_MAX);
    targetTau(this.comb.fb.gain, fb, t, 0.05);
    targetTau(this.comb.wet.gain, (1 - fb) * combWetScale(this.hz, this.ctx.sampleRate), t, 0.05);
  }

  /** Live editor edits (type changes need a new voice; everything else is re-targeted). */
  update(spec: FilterSpec, macros: MacroMap, t: number): void {
    this.spec = spec;
    this.macros = macros;
    this.applyCutoff(t, 0.2);
    if (this.biquad) {
      targetTau(this.biquad.Q, spec.q, t, 0.1);
      if (this.lfo) { this.lfo.setDepth(this.lfoDepthHz(), t); this.lfo.setRate(spec.lfo.rateHz, t); }
      else if (spec.lfo.depth > 0 && spec.lfo.rateHz > 0) {
        this.lfo = new LfoRoute(this.ctx, { shape: spec.lfo.shape, rateHz: spec.lfo.rateHz, depth: this.lfoDepthHz() }, this.biquad.frequency);
      }
    }
    if (this.comb) {
      const fb = clamp(spec.combFeedback, 0, COMB_FEEDBACK_MAX);
      targetTau(this.comb.fb.gain, fb, t, 0.1);
      targetTau(this.comb.wet.gain, (1 - fb) * combWetScale(this.hz, this.ctx.sampleRate), t, 0.1);
    }
  }

  get type(): FilterSpec['type'] { return this.kind; }

  dispose(): void {
    this.untick?.();
    this.lfo?.dispose();
    for (const n of [this.input, this.output, this.biquad, this.formant?.f1, this.formant?.f2, this.comb?.delay, this.comb?.fb, this.comb?.damp, this.comb?.wet, this.comb?.clip]) {
      try { n?.disconnect(); } catch { /* gone */ }
    }
  }
}
